import { IConfigurbaleMarketingWidget } from 'app/_components/ContentfulPage/ConfigurableMarketingWidget/ConfigurableMarketingWidget.types'
import { Entry } from 'contentful'
import { parseConfigurableCta } from './parseConfigurableCta'
import { IContentfulConfigurableCtaEntry } from 'types/contentful/configurableCta.types'
import { IConfigurableText } from 'app/_components/ContentfulPage/ConfigurableText/ConfigurableText.types'
import {
  convertFormSpecificationConsentOptions,
  getFormSpecification,
} from '@utils/form/hubspot'
import {
  TFieldSpecification,
  THubspotFormSpecification,
} from '@utils/form/hubspot/schema'
import { TConsentOptions } from '@utils/form/schema'
import {
  NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_COUNTRY,
  regionsWithStrictOptInPolicy,
} from '@utils/client-side-config'
import { IFormField } from '@noissue-ui-kit/form/form'
import { TInputValidator } from '@noissue-ui-kit/oldBranding/formInput/FormInput.utils'
import { RESERVED_CONSENT_FIELD_NAME } from '@utils/hubspot'

const SUPPORTED_CONSENT_TYPES = ['implicit_consent_to_process']

const getFormFieldComponent = ({
  name,
  fieldType,
  defaultValue,
  required,
  hidden,
  label,
  placeholder,
  description,
}: TFieldSpecification): IFormField => {
  const baseFieldData = {
    name,
    label,
    placeholder,
    description,
    initialValue: defaultValue,
    className: `${hidden ? 'hidden' : ''}`,
  }

  // For now forms inside marketing popup support only
  // text input, email input and checkbox fields, extende it with other form fields
  // components from nossiue ui kit when needed.
  switch (fieldType) {
    case 'email':
    case 'single_line_text':
      return {
        ...baseFieldData,
        type: fieldType === 'email' ? 'email' : 'text',
        initialValue: '',
        validators: [
          fieldType === 'email' ? 'email' : null,
          required ? 'required' : null,
        ].filter(Boolean) as TInputValidator[],
      }

    case 'single_checkbox':
      return {
        ...baseFieldData,
        type: 'checkbox',
        validators: [required ? 'required' : null].filter(
          Boolean
        ) as TInputValidator[],
      }

    default:
      return null
  }
}

const parseMarketingForm = (
  formSpecification: THubspotFormSpecification
): IFormField[] => {
  const components = []

  if (!formSpecification?.fieldGroups) {
    return []
  }

  for (const fieldGroup of formSpecification?.fieldGroups) {
    const { fields } = fieldGroup

    if (!fields) {
      // No fields mean it may only be text
      continue
    }

    const fieldSpec = fields[0]
    const component = getFormFieldComponent(fieldSpec)
    components.push(component)
  }

  return components?.filter(Boolean)
}

export const parseMarketingBanner = async (
  contentfulMarketingBanner: Entry<
    Omit<
      IConfigurbaleMarketingWidget,
      | 'bannerTitleConfig'
      | 'bannerCta'
      | 'popupMarketingForm'
      | 'popupMarketingFormConsentOptions'
    > & {
      bannerCta: Entry<IContentfulConfigurableCtaEntry>
      bannerTitleConfig: Entry<IConfigurableText>
      componentKey: string
    }
  >
): Promise<IConfigurbaleMarketingWidget & { componentKey: string }> => {
  const formId = contentfulMarketingBanner?.fields?.popupMarketingFormId

  if (!formId) {
    return null
  }

  const formConfig: THubspotFormSpecification = await getFormSpecification(
    formId
  )
  const formComponents: IFormField[] = parseMarketingForm(formConfig)

  // On Hubspot side you can choose among different consent options
  // Forms rendered as part of MarketingBanner support only [implicit_consent_to_process] option for now
  // Adjust it if extension is needed
  const hasLegalConsentOptions = SUPPORTED_CONSENT_TYPES.includes(
    formConfig?.legalConsentOptions?.type
  )

  const parsedConsentOptions: TConsentOptions = hasLegalConsentOptions
    ? convertFormSpecificationConsentOptions(formConfig)
    : null

  const consentCheckbox: IFormField = parsedConsentOptions
    ? getFormFieldComponent({
        name: RESERVED_CONSENT_FIELD_NAME,
        label: parsedConsentOptions.label,
        description: parsedConsentOptions.description,
        hidden:
          !regionsWithStrictOptInPolicy[
            NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_COUNTRY
          ],
        fieldType: 'single_checkbox',
        defaultValue: regionsWithStrictOptInPolicy[
          NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_COUNTRY
        ]
          ? false
          : true,
        required: parsedConsentOptions.required,
      })
    : null

  return {
    ...contentfulMarketingBanner?.fields,
    bannerCta: parseConfigurableCta(
      contentfulMarketingBanner?.fields?.bannerCta
    ),
    bannerTitleConfig: {
      ...contentfulMarketingBanner?.fields?.bannerTitleConfig?.fields,
    },
    componentKey: 'configurableMarketingBanner',
    popupMarketingForm: [...formComponents, consentCheckbox].filter(Boolean),
    popupMarketingFormConsentOptions: parsedConsentOptions,
  }
}
